import { ref, reactive, readonly } from "vue";
import launchDarklyService from "@/services/launchdarkly";

// Global state shared between all instances
const globalFlags = reactive({});
const isLoading = ref(false);
const isInitialized = ref(false);
const errorState = ref(null);

export function useFeatureFlags() {
  /**
   * Loads an individual flag from LaunchDarkly
   * @param {string} flagKey - Flag name
   * @param {*} defaultValue - Default value if flag is not available
   * @returns {Promise<*>} - Flag value
   */
  const loadFlag = async (flagKey, defaultValue = false) => {
    try {
      const value = await launchDarklyService.getFlag(flagKey, defaultValue);
      globalFlags[flagKey] = value;
      errorState.value = null;
      return value;
    } catch (error) {
      console.error(`Error loading flag ${flagKey}:`, error);
      globalFlags[flagKey] = defaultValue;
      errorState.value = error;
      return defaultValue;
    }
  };

  /**
   * Loads multiple flags at once
   * @param {Object} flagsConfig - Object with flagKey: defaultValue
   * @returns {Promise<Object>} - Object with loaded values
   */
  const loadFlags = async (flagsConfig) => {
    isLoading.value = true;
    errorState.value = null;

    try {
      const promises = Object.entries(flagsConfig).map(async ([key, defaultValue]) => {
        const value = await loadFlag(key, defaultValue);
        return { key, value };
      });

      const results = await Promise.all(promises);
      isInitialized.value = true;

      // Return object with results
      return results.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});
    } catch (error) {
      console.error("Error loading multiple flags:", error);
      errorState.value = error;
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Gets flag value from cache
   * @param {string} flagKey - Flag name
   * @param {*} defaultValue - Default value if flag doesn't exist
   * @returns {*} - Flag value
   */
  const getFlag = (flagKey, defaultValue = false) => {
    return globalFlags[flagKey] ?? defaultValue;
  };

  /**
   * Checks if a flag exists in cache
   * @param {string} flagKey - Flag name
   * @returns {boolean} - True if flag exists
   */
  const hasFlag = (flagKey) => {
    return flagKey in globalFlags;
  };

  /**
   * Refreshes a specific flag
   * @param {string} flagKey - Flag name
   * @param {*} defaultValue - Default value
   * @returns {Promise<*>} - New flag value
   */
  const refreshFlag = async (flagKey, defaultValue = false) => {
    return await loadFlag(flagKey, defaultValue);
  };

  /**
   * Refreshes all loaded flags
   * @returns {Promise<void>}
   */
  const refreshAllFlags = async () => {
    if (Object.keys(globalFlags).length === 0) {
      console.warn("No flags to refresh. Load flags first.");
      return;
    }

    isLoading.value = true;

    try {
      const flagKeys = Object.keys(globalFlags);
      const promises = flagKeys.map(async (key) => {
        const currentValue = globalFlags[key];
        return await loadFlag(key, currentValue);
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Error refreshing flags:", error);
      errorState.value = error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clears the flags cache
   */
  const clearFlags = () => {
    Object.keys(globalFlags).forEach((key) => {
      delete globalFlags[key];
    });
    isInitialized.value = false;
    errorState.value = null;
  };

  /**
   * Sets a flag manually (useful for testing)
   * @param {string} flagKey - Flag name
   * @param {*} value - Flag value
   */
  const setFlag = (flagKey, value) => {
    globalFlags[flagKey] = value;
  };

  /**
   * Gets all flags as a plain object
   * @returns {Object} - Copy of all flags
   */
  const getAllFlags = () => {
    return { ...globalFlags };
  };

  return {
    // Reactive state (read-only)
    flags: readonly(globalFlags),
    isLoading: readonly(isLoading),
    isInitialized: readonly(isInitialized),
    error: readonly(errorState),

    // Methods for loading flags
    loadFlag,
    loadFlags,
    refreshFlag,
    refreshAllFlags,

    // Methods for accessing flags
    getFlag,
    hasFlag,
    getAllFlags,

    // Utility methods
    clearFlags,
    setFlag, // Mainly for testing
  };
}

/**
 * Global shared instance to use throughout the application
 */
let globalInstance = null;

export function useGlobalFeatureFlags() {
  if (!globalInstance) {
    globalInstance = useFeatureFlags();
  }
  return globalInstance;
}

/**
 * Convenience function to load common application flags
 * @returns {Promise<Object>} - Loaded flags
 */
export async function loadCommonFlags() {
  const flags = useGlobalFeatureFlags();

  return await flags.loadFlags({
    // Common application flags
    "show-deactivate-option": false,
    "employee-access-management": true,
    "role-management-v2": false,
    "bulk-operations": false,
    "employee.page_horizontal_menu.enabled": false,
    "catalogs.dashboard.enabled": false,
    // Add more flags here as needed
  });
}
